import React, { Fragment, useEffect, useState } from 'react';
import '../style.scss';
import { Icon, LoadingSpinner } from 'legacy-packages/ft-cb';
import cloneDeep from 'lodash/cloneDeep';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import A11y from 'chameleon/ui-stack/utilities/a11y';

const PerformanceTierPopover = ({
  show,
  label,
  resultsData,
  onClose,
  studentsScored,
  handleNumStudentsClick,
  index,
  id,
  questionIdsInString,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    noResults: [],
    firstQuartile: [],
    secondQuartile: [],
    thirdQuartile: [],
    fourthQuartile: [],
  });
  useEffect(() => {
    setShowModal(show);
    const sortedData = sortStudents(resultsData);
    setModalData(sortedData);
  }, [resultsData]);

  const closeModal = () => {
    setShowModal(!showModal);
    setModalData({
      noResults: [],
      firstQuartile: [],
      secondQuartile: [],
      thirdQuartile: [],
      fourthQuartile: [],
    });
    onClose();
  };

  const hasResolvedStudentData = () => {
    const {
      noResults,
      firstQuartile,
      secondQuartile,
      thirdQuartile,
      fourthQuartile,
    } = modalData;

    return ![
      noResults,
      firstQuartile,
      secondQuartile,
      thirdQuartile,
      fourthQuartile,
    ].every((quartile) => !quartile || quartile.length === 0);
  };

  const sortStudents = (studentData) => {
    // Sort performance tiers by score, then by last name, then by first name
    const _studentData = cloneDeep(studentData); // Create a copy of studentData so that we don't change the array
    // eslint-disable-next-line no-unused-vars
    for (const [performanceTier, studentsAndScores] of Object.entries(
      _studentData,
    )) {
      if (performanceTier && studentsAndScores) {
        _studentData[performanceTier] = studentsAndScores.sort((a, b) =>
          a.firstName.localeCompare(b.firstName),
        );
        _studentData[performanceTier] = studentsAndScores.sort((a, b) =>
          a.lastName.localeCompare(b.lastName),
        );
        if (performanceTier !== 'noResults') {
          _studentData[performanceTier] = studentsAndScores.sort(
            (a, b) => b.score - a.score,
          );
        }
      }
    }
    return _studentData;
  };

  return (
    <PopOver
      key={id}
      showCloseButton
      withPortal
      className="performance-tier-popover"
      size="wide"
      position="end"
      accessibility={{
        'aria-label': ``,
        'aria-haspopup': 'listbox',
      }}
      onClose={closeModal}
      popoverTitle={label}
      trigger={
        <A11y role="button">
          {studentsScored > 0 ? (
            <a
              onClick={() =>
                handleNumStudentsClick(index, id, questionIdsInString)
              }
            >
              {studentsScored}
            </a>
          ) : (
            <span>{studentsScored}</span>
          )}
        </A11y>
      }
    >
      <Fragment>
        <div className="performanceTiersModal">
          <div className="performanceTiersModalContent">
            <div className="performanceTiersModalBody">
              {!hasResolvedStudentData() ? (
                <div className="spinnerContainer">
                  <LoadingSpinner />
                </div>
              ) : (
                <React.Fragment>
                  <div className="tierContainer mt-6">
                    <div className="userCountContainer">
                      <Icon name="person" size={16} />{' '}
                      <span>
                        {modalData.noResults && modalData.noResults.length}
                      </span>
                    </div>
                    <div className="quartileHeader quartileHeader--noResults">
                      No Results
                    </div>
                    <div className="columnDataContainer">
                      {modalData.noResults &&
                        modalData.noResults.map((noResultItem) => (
                          <div
                            className="studentAndPercentageContainer"
                            key={noResultItem.firstName + noResultItem.lastName}
                          >
                            <p className="ml-3">
                              {noResultItem.firstName} {noResultItem.lastName}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                  <div className="tierContainer mt-6">
                    <div className="userCountContainer">
                      <Icon name="person" size={16} />{' '}
                      <span>
                        {modalData.firstQuartile &&
                          modalData.firstQuartile.length}
                      </span>
                    </div>
                    <div className="quartileHeader quartileHeader--first">
                      0 to 24.99%
                    </div>
                    <div className="columnDataContainer">
                      {modalData.firstQuartile &&
                        modalData.firstQuartile.map(
                          (firstQuartileItem, idx) => (
                            <div
                              className="studentAndPercentageContainer"
                              key={
                                firstQuartileItem.firstName +
                                firstQuartileItem.lastName -
                                `${idx}`
                              }
                            >
                              <p className="ml-3">
                                {firstQuartileItem.firstName}{' '}
                                {firstQuartileItem.lastName}
                              </p>
                              <p className="mr-3 d-flex flex-column justify-center">
                                {Math.round(firstQuartileItem.score)}%
                              </p>
                            </div>
                          ),
                        )}
                    </div>
                  </div>
                  <div className="tierContainer mt-6">
                    <div className="userCountContainer">
                      <Icon name="person" size={16} />{' '}
                      <span>
                        {modalData.secondQuartile &&
                          modalData.secondQuartile.length}
                      </span>
                    </div>
                    <div className="quartileHeader quartileHeader--second">
                      25 to 49.99%
                    </div>
                    <div className="columnDataContainer">
                      {modalData.secondQuartile &&
                        modalData.secondQuartile.map(
                          (secondQuartileItem, idx) => (
                            <div
                              className="studentAndPercentageContainer"
                              key={
                                secondQuartileItem.firstName +
                                secondQuartileItem.lastName -
                                `${idx}`
                              }
                            >
                              <p className="ml-3">
                                {secondQuartileItem.firstName}{' '}
                                {secondQuartileItem.lastName}
                              </p>
                              <p className="mr-3 d-flex flex-column justify-center">
                                {Math.round(secondQuartileItem.score)}%
                              </p>
                            </div>
                          ),
                        )}
                    </div>
                  </div>
                  <div className="tierContainer mt-6">
                    <div className="userCountContainer">
                      <Icon name="person" size={16} />{' '}
                      <span>
                        {modalData.thirdQuartile &&
                          modalData.thirdQuartile.length}
                      </span>
                    </div>
                    <div className="quartileHeader quartileHeader--third">
                      50 to 74.99%
                    </div>
                    <div className="columnDataContainer">
                      {modalData.thirdQuartile &&
                        modalData.thirdQuartile.map(
                          (thirdQuartileItem, idx) => {
                            return (
                              <div
                                className="studentAndPercentageContainer"
                                key={
                                  thirdQuartileItem.firstName +
                                  thirdQuartileItem.lastName -
                                  `${idx}`
                                }
                              >
                                <p className="ml-3">
                                  {thirdQuartileItem.firstName}{' '}
                                  {thirdQuartileItem.lastName}
                                </p>
                                <p className="mr-3 d-flex flex-column justify-center">
                                  {Math.round(thirdQuartileItem.score)}%
                                </p>
                              </div>
                            );
                          },
                        )}
                    </div>
                  </div>
                  <div className="tierContainer mt-6">
                    <div className="userCountContainer">
                      <Icon name="person" size={16} />{' '}
                      <span>
                        {modalData.fourthQuartile
                          ? modalData.fourthQuartile.length
                          : '-'}
                      </span>
                    </div>
                    <div className="quartileHeader quartileHeader--fourth">
                      75 to 100%
                    </div>
                    <div className="columnDataContainer">
                      {modalData.fourthQuartile &&
                        modalData.fourthQuartile.map(
                          (fourthQuartileItem, idx) => {
                            return (
                              <div
                                className="studentAndPercentageContainer"
                                key={
                                  fourthQuartileItem.firstName +
                                  fourthQuartileItem.lastName -
                                  `${idx}`
                                }
                              >
                                <p className="ml-3">
                                  {fourthQuartileItem.firstName}{' '}
                                  {fourthQuartileItem.lastName}
                                </p>
                                <p className="mr-3 d-flex flex-column justify-center">
                                  {Math.round(fourthQuartileItem.score)}%
                                </p>
                              </div>
                            );
                          },
                        )}
                    </div>
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    </PopOver>
  );
};

export default PerformanceTierPopover;
